import React, { useState } from 'react';
import { Row, Col, Card, Typography, Progress, Tooltip, Alert } from 'antd';
import ParameterModal from './shared/ParameterModal';
import SVGCoxcombChart from './SVGCoxcombChart';

const { Title, Text } = Typography;

const HealthSystemsOverview = ({ systemDeviations, lifestyleDeviations, tableData, allParamsConfig, categorizedNutritionParameters }) => {

  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalData, setModalData] = useState([]);
  const [error, setError] = useState(null);

  // Error boundary for this component
  if (error) {
    return (
      <Alert
        message="Health Systems Overview Error"
        description={`An error occurred while loading the health systems overview: ${error.message}`}
        type="error"
        showIcon
        style={{ margin: '20px' }}
        action={
          <button onClick={() => setError(null)} style={{ border: 'none', background: 'none', color: '#1890ff', cursor: 'pointer' }}>
            Retry
          </button>
        }
      />
    );
  }

  // Helper function to get all parameters associated with a health system based on parameter weights
  // This uses the EXACT same logic as PatientPortalHealthOverview.js
  const getParametersForHealthSystem = (systemKey, allParamsConfig, tableData) => {
    const associatedParams = [];

    if (!allParamsConfig || !tableData) {
      console.warn(`[HealthSystemsOverview] Missing allParamsConfig or tableData for system: ${systemKey}`);
      return associatedParams;
    }

    // Create a map of available parameters from tableData for quick lookup
    const availableParamsMap = new Map();
    (tableData || []).forEach(categoryGroup => {
      (categoryGroup.parameters || []).forEach(param => {
        availableParamsMap.set(param.key, {
          ...param,
          // Use main category name to match Priority Cards categorization
          category: categoryGroup.categoryName || param.category || 'Uncategorized'
        });
      });
    });

    // Check all parameters in the configuration to see if they're associated with this system
    Object.entries(allParamsConfig).forEach(([paramKey, paramConfig]) => {
      if (paramConfig.systems && paramConfig.systems[systemKey]) {
        const systemWeights = paramConfig.systems[systemKey];

        // Check if this parameter has any non-zero weight for this system
        const hasSystemAssociation = Object.values(systemWeights).some(weight =>
          (typeof weight === 'number' && weight > 0) ||
          (typeof weight === 'object' && weight.final_weight > 0)
        );

        if (hasSystemAssociation) {
          const paramData = availableParamsMap.get(paramKey);
          if (paramData) {
            // Parameter has data available
            associatedParams.push({
              ...paramData,
              hasData: true,
              isAssociated: true
            });
          } else {
            // Parameter is associated but no data available
            // Map parameter key to main category to match Priority Cards categorization
            let mainCategory = 'Uncategorized';
            if (paramKey.startsWith('vitals_')) mainCategory = 'Vitals';
            else if (paramKey.startsWith('labs_')) mainCategory = 'Lab Results';
            else if (paramKey.startsWith('pe_')) mainCategory = 'Physical Exam';
            else if (paramKey.startsWith('biometrics_')) mainCategory = 'Biometrics';
            else if (paramKey.startsWith('imaging_')) mainCategory = 'Imaging';
            else if (paramKey.startsWith('lifestyle_')) mainCategory = 'Lifestyle';

            associatedParams.push({
              key: paramKey,
              parameterName: paramConfig.displayName || paramKey,
              category: mainCategory,
              hasData: false,
              isAssociated: true,
              currentValue: null,
              idealTarget: null,
              zScore: null,
              absoluteZScore: 0,
              weightedAbsoluteZScore: 0
            });
          }
        }
      }
    });

    console.log(`[HealthSystemsOverview] Found ${associatedParams.length} parameters associated with ${systemKey} system`);
    return associatedParams;
  };

  const handleSystemCardClick = (systemName, data) => {
    try {
      if (!systemName || !data) {
        console.warn("handleSystemCardClick called with invalid arguments", { systemName, data });
        return;
      }

      // Use the pre-calculated contributing parameters from the scoring engine
      // We use the 'structure' dimension's list, as they are all identical now.
      const contributingParams = data.structure?.contributingParams || [];

      // --- START: New UI Filtering Logic ---
      const filteredParams = contributingParams.filter(param => {
        // The `averageSystemWeight` is now directly available on each parameter object
        return param.averageSystemWeight >= 25;
      });
      // --- END: New UI Filtering Logic ---

      // The parameters are already sorted by `priorityScore` from the engine, so no need to re-sort.
      
      // Group by original clinical category
      const groupedByCategory = filteredParams.reduce((acc, param) => {
        const categoryName = param.category || 'Uncategorized';
        if (!acc[categoryName]) {
          acc[categoryName] = [];
        }
        acc[categoryName].push(param);
        return acc;
      }, {});

      const displayName = systemName.charAt(0).toUpperCase() + systemName.slice(1);

      setModalTitle(`${displayName} System Parameters`);
      setModalData({
        grouped: groupedByCategory,
        flat: filteredParams,
      });
      setModalVisible(true);

    } catch (error) {
      console.error(`[HealthSystemsOverview] Error in handleSystemCardClick for ${systemName}:`, error);
      setError(error);
    }
  };

  const handleLifestyleCardClick = (domainName, data) => {
    try {
        const systemInfo = getSystemDescription(domainName);
        const displayName = systemInfo.title || domainName.charAt(0).toUpperCase() + domainName.slice(1);
        let paramsToDisplay = [];

        if (domainName === 'nutrition') {
            const allParamsMap = new Map();
            tableData.forEach(group => {
                (group.parameters || []).forEach(p => allParamsMap.set(p.key, p));
            });

            categorizedNutritionParameters.forEach(subCategory => {
                // Handle both flat parameters and nested subcategories
                if (subCategory.parameters) {
                    // Flat structure (Dietary Habits, Eating Behaviors)
                    subCategory.parameters.forEach(paramKey => {
                        if (allParamsMap.has(paramKey)) {
                            paramsToDisplay.push(allParamsMap.get(paramKey));
                        }
                    });
                } else if (subCategory.subcategories) {
                    // Nested structure (Lab markers, etc.)
                    subCategory.subcategories.forEach(nestedSubCategory => {
                        (nestedSubCategory.parameters || []).forEach(paramKey => {
                            if (allParamsMap.has(paramKey)) {
                                paramsToDisplay.push(allParamsMap.get(paramKey));
                            }
                        });
                    });
                }
            });
        } else {
            // For other domains, use the pre-calculated contributingParams
            paramsToDisplay = (data.contributingParams || []).map(p => {
                const fullParam = tableData.flatMap(g => g.parameters || []).find(fp => fp.key === p.key);
                return fullParam || p; // Fallback to the partial data if full not found
            });
        }

        const processedParams = paramsToDisplay.map(param => {
            const priorityScore = typeof param.weightedAbsoluteZScore === 'number' ? param.weightedAbsoluteZScore : 0;
            let priority = 'Normal';
            if (priorityScore > 1.5) priority = 'High'; // Using lifestyle thresholds
            else if (priorityScore > 0.75) priority = 'Moderate';
            else if (priorityScore > 0.25) priority = 'Low';

            return {
                ...param,
                name: param.parameterName || param.key,
                displayName: param.parameterName || param.key,
                specificImpact: priorityScore,
                priority: priority,
                normalRange: param.normalRange,
                sd: param.sd,
            };
        });

        const sortedFlatList = [...processedParams].sort((a, b) => (b.specificImpact || 0) - (a.specificImpact || 0));

        let groupedByCategory = {};
        if (domainName === 'nutrition') {
            const allParamsMap = new Map(sortedFlatList.map(p => [p.key, p]));
            groupedByCategory = categorizedNutritionParameters.reduce((acc, subCategory) => {
                if (subCategory.parameters) {
                    // Flat structure (Dietary Habits, Eating Behaviors)
                    acc[subCategory.name] = (subCategory.parameters || [])
                        .map(key => allParamsMap.get(key))
                        .filter(Boolean) // Filter out params that might not have data
                        .sort((a, b) => (b.specificImpact || 0) - (a.specificImpact || 0));
                } else if (subCategory.subcategories) {
                    // Nested structure (Lab markers, etc.) - flatten into main category
                    subCategory.subcategories.forEach(nestedSubCategory => {
                        acc[`${subCategory.name} - ${nestedSubCategory.name}`] = (nestedSubCategory.parameters || [])
                            .map(key => allParamsMap.get(key))
                            .filter(Boolean) // Filter out params that might not have data
                            .sort((a, b) => (b.specificImpact || 0) - (a.specificImpact || 0));
                    });
                }
                return acc;
            }, {});
        } else {
            // Simple grouping for other lifestyle domains
            groupedByCategory = {
                [displayName]: sortedFlatList,
            };
        }

        setModalTitle(`${displayName} Domain Parameters`);
        setModalData({
            grouped: groupedByCategory,
            flat: sortedFlatList,
        });
        setModalVisible(true);

    } catch (error) {
        console.error(`[HealthSystemsOverview] Error in handleLifestyleCardClick for ${domainName}:`, error);
        setError(error);
    }
  };

  // Get PNG icon path for health systems and lifestyle domains (matching SVGCoxcombChart)
  const getPNGIconPath = (name) => {
    const normalizedName = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();

    const iconMap = {
      // Health Systems - PNG Image Paths (matching SVGCoxcombChart)
      'Neurological': '/icons/Neurological icon.png',
      'Cardiovascular': '/icons/Cardiovascular icon.png',
      'Respiratory': '/icons/Respiratory icon.png',
      'Metabolic': '/icons/Metabolic icon.png',
      'Filtration': '/icons/Filtration icon.png',
      'Musculoskeletal': '/icons/Musculoskeletal icon.png',
      'Immune': '/icons/Immune icon.png',

      // Lifestyle Domains - PNG Image Paths (matching SVGCoxcombChart)
      'Nutrition': '/icons/Nutrition icon.png',
      'Movement': '/icons/Movement icon.png',
      'Recovery': '/icons/Recovery icon.png',
      'Mental_health': '/icons/Emotion icon.png',
      'Mental health': '/icons/Emotion icon.png',
      'Emotion': '/icons/Emotion icon.png',
      'Environment': '/icons/Environment icon.png',
    };

    return iconMap[normalizedName] || null;
  };

  // Render PNG icon component
  const renderPNGIcon = (name) => {
    const iconPath = getPNGIconPath(name);

    if (!iconPath) {
      // Fallback to a simple div if icon not found
      return (
        <div style={{
          width: '48px',
          height: '48px',
          backgroundColor: '#f0f0f0',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto',
          fontSize: '24px',
          color: '#999'
        }}>
          ?
        </div>
      );
    }

    return (
      <div style={{
        width: '48px',
        height: '48px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto'
      }}>
        <img
          src={iconPath}
          alt={`${name} icon`}
          style={{
            width: '40px',
            height: '40px',
            objectFit: 'contain',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
          }}
        />
      </div>
    );
  };
  
  const getScoreColor = (score) => {
    if (score < 70) return '#ff4d4f';
    if (score < 85) return '#faad14';
    return '#52c41a';
  };

  const getRiskScoreColor = (riskScore) => {
    if (riskScore < 15) return '#52c41a'; // Low risk is green
    if (riskScore < 30) return '#faad14'; // Moderate risk is yellow
    return '#ff4d4f'; // High risk is red
  };

  // Get patient-friendly descriptions for health systems and lifestyle domains
  const getSystemDescription = (name) => {
    const descriptions = {
      // Health Systems
      'neurological': {
        title: 'Neurological System',
        description: 'Your brain and nervous system health, including cognitive function, memory, and neurological markers. This system controls thinking, coordination, and communication throughout your body.',
        role: 'Controls brain function, memory, and nervous system communication'
      },
      'cardiovascular': {
        title: 'Cardiovascular System',
        description: 'Your heart and blood vessel health, including blood pressure, cholesterol, and circulation. This system pumps blood and delivers oxygen and nutrients throughout your body.',
        role: 'Pumps blood and delivers oxygen throughout your body'
      },
      'respiratory': {
        title: 'Respiratory System',
        description: 'Your lung and breathing health, including oxygen levels and respiratory function. This system brings oxygen into your body and removes carbon dioxide.',
        role: 'Brings oxygen into your body and removes waste gases'
      },
      'metabolic': {
        title: 'Metabolic System',
        description: 'Your energy production and metabolism, including blood sugar, thyroid function, and energy processing. This system converts food into energy and regulates your metabolic rate.',
        role: 'Converts food into energy and regulates metabolism'
      },
      'filtration': {
        title: 'Filtration System',
        description: 'Your kidney and liver health, including waste removal and detoxification. This system filters toxins from your blood and maintains fluid balance.',
        role: 'Filters waste from blood and maintains fluid balance'
      },
      'musculoskeletal': {
        title: 'Musculoskeletal System',
        description: 'Your muscle, bone, and joint health, including strength, mobility, and structural integrity. This system provides movement, support, and protection for your body.',
        role: 'Provides movement, support, and structural integrity'
      },
      'immune': {
        title: 'Immune System',
        description: 'Your body\'s defense system, including infection resistance and inflammatory markers. This system protects you from diseases and helps heal injuries.',
        role: 'Protects against diseases and helps heal injuries'
      },

      // Lifestyle Domains
      'nutrition': {
        title: 'Nutrition',
        description: 'Your eating habits and nutritional status, including diet quality, nutrient intake, and eating patterns that support optimal health.',
        role: 'Provides essential nutrients for optimal body function'
      },
      'movement': {
        title: 'Movement & Exercise',
        description: 'Your physical activity levels and exercise habits, including cardiovascular fitness, strength training, and daily movement patterns.',
        role: 'Maintains fitness, strength, and overall physical health'
      },
      'recovery': {
        title: 'Recovery & Sleep',
        description: 'Your sleep quality and recovery patterns, including sleep duration, sleep quality, and rest periods that allow your body to repair and recharge.',
        role: 'Allows body repair, memory consolidation, and energy restoration'
      },
      'mental_health': {
        title: 'Mental Health & Wellbeing',
        description: 'Your emotional and mental wellness, including stress management, mood, and psychological health factors that affect overall wellbeing.',
        role: 'Supports emotional balance and psychological resilience'
      },
      'mental health': {
        title: 'Mental Health & Wellbeing',
        description: 'Your emotional and mental wellness, including stress management, mood, and psychological health factors that affect overall wellbeing.',
        role: 'Supports emotional balance and psychological resilience'
      }
    };

    const key = name.toLowerCase().replace(/\s+/g, '_');
    return descriptions[key] || {
      title: name.charAt(0).toUpperCase() + name.slice(1),
      description: `Health measurements and indicators related to your ${name.toLowerCase()} system.`,
      role: `Supports your overall health and wellbeing`
    };
  };

  // Transform system deviations data for SVGCoxcombChart
  const transformSystemDataForCoxcomb = () => {
    if (!systemDeviations || Object.keys(systemDeviations).length === 0) return [];

    return Object.entries(systemDeviations).map(([sysKey, dimensions]) => {
      const structureScore = dimensions.structure?.healthScore ?? 100;
      const functionScore = dimensions.function?.healthScore ?? 100;
      const riskScore = dimensions.risk?.healthScore ?? 0; // Default to 0 risk

      // Invert risk score for overall health calculation, but cap at 100
      const riskHealthEquivalent = Math.max(0, 100 - riskScore);
      const averageSystemHealth = Math.round((structureScore + functionScore + riskHealthEquivalent) / 3);

      const normalizedSysKey = sysKey.charAt(0).toUpperCase() + sysKey.slice(1).toLowerCase();

      return {
        name: normalizedSysKey,
        score: averageSystemHealth
      };
    });
  };

  // Transform lifestyle deviations data for SVGCoxcombChart
  const transformLifestyleDataForCoxcomb = () => {
    if (!lifestyleDeviations || Object.keys(lifestyleDeviations).length === 0) return [];

    return Object.entries(lifestyleDeviations).map(([domainKey, data]) => {
      const domainHealthScore = data.healthScore !== null && data.healthScore !== undefined ? Math.round(data.healthScore) : 0;

      let normalizedDomainKey = domainKey.charAt(0).toUpperCase() + domainKey.slice(1).toLowerCase();
      if (normalizedDomainKey.toLowerCase().replace(/[\s_]/g, "") === "mentalhealth") {
        normalizedDomainKey = "Mental Health";
      } else {
        normalizedDomainKey = normalizedDomainKey.replace("_", " ");
        normalizedDomainKey = normalizedDomainKey.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
      }

      return {
        name: normalizedDomainKey,
        score: domainHealthScore
      };
    });
  };

  const renderDomainCard = (name, data, totalAvailableParams) => {
    const structureScore = data.structure?.healthScore ?? 100;
    const functionScore = data.function?.healthScore ?? 100;
    const riskScore = data.risk?.healthScore ?? 0; // Default to 0 risk

    // Invert risk score for overall health calculation, but cap at 100
    const riskHealthEquivalent = Math.max(0, 100 - riskScore);
    const overallScore = (structureScore + functionScore + riskHealthEquivalent) / 3;

    const systemInfo = getSystemDescription(name);
    const displayName = name.charAt(0).toUpperCase() + name.slice(1);

    return (
      <Col xs={24} sm={12} md={8} lg={6} key={name}>
        <Card
          hoverable
          style={{
            textAlign: 'center',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onClick={() => handleSystemCardClick(name, data)}
        >
          <Tooltip title={systemInfo.description}>
            {renderPNGIcon(name)}
            <Title level={5} style={{ marginTop: '10px', marginBottom: '15px' }}>
              {displayName}
            </Title>
          </Tooltip>
          <Progress
            type="circle"
            percent={overallScore}
            format={(percent) => `${percent.toFixed(1)}%`}
            strokeColor={getScoreColor(overallScore)}
            size={80}
          />
          <div style={{ marginTop: '15px', fontSize: '12px', lineHeight: '1.4' }}>
            <Tooltip title="Reflects the physical integrity and composition of the system.">
              <div style={{ marginBottom: '4px' }}>
                <Text strong>Structure:</Text> <Text style={{ color: getScoreColor(structureScore) }}>{structureScore.toFixed(1)}%</Text>
              </div>
            </Tooltip>
            <Tooltip title="Reflects how well the system is performing its duties.">
              <div style={{ marginBottom: '4px' }}>
                <Text strong>Function:</Text> <Text style={{ color: getScoreColor(functionScore) }}>{functionScore.toFixed(1)}%</Text>
              </div>
            </Tooltip>
            <Tooltip title="Represents the risk of future health issues. A lower score is better.">
              <div style={{ marginBottom: '8px' }}>
                <Text strong>Risk Score:</Text> <Text style={{ color: getRiskScoreColor(riskScore) }}>{riskScore.toFixed(1)}%</Text>
              </div>
            </Tooltip>
          </div>
        </Card>
      </Col>
    );
  };

  const renderLifestyleDomainCard = (name, data) => {
    const score = data.healthScore !== null && data.healthScore !== undefined ? data.healthScore : 0;
    const systemInfo = getSystemDescription(name);
    const displayName = systemInfo.title || name.charAt(0).toUpperCase() + name.slice(1);
    const count = data.count || 0;

    return (
      <Col xs={24} sm={12} md={8} lg={6} key={name}>
        <Tooltip
          title={
            <div style={{ maxWidth: '300px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1890ff' }}>
                {displayName}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Role:</strong> {systemInfo.role}
              </div>
              <div>
                {systemInfo.description}
              </div>
            </div>
          }
          placement="top"
        >
          <Card
            hoverable
            style={{
              textAlign: 'center',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onClick={() => handleLifestyleCardClick(name, data)}
          >
            {renderPNGIcon(name)}
            <Title level={5} style={{ marginTop: '10px', marginBottom: '15px' }}>
              {displayName}
            </Title>
            <Progress
              type="circle"
              percent={Math.round(score)}
              format={(percent) => `${percent}%`}
              strokeColor={getScoreColor(score)}
              size={80}
            />
            <div style={{ marginTop: '15px', fontSize: '12px', color: '#6c757d' }}>
              <Text strong>{count}</Text> parameters assessed
            </div>
          </Card>
        </Tooltip>
      </Col>
    );
  };

  // Prepare chart data
  const systemChartData = transformSystemDataForCoxcomb();
  const lifestyleChartData = transformLifestyleDataForCoxcomb();

  // Centralized calculation for the total number of available parameters
  const totalAvailableParams = (tableData || []).reduce((acc, categoryGroup) => {
    return acc + (categoryGroup.parameters ? categoryGroup.parameters.length : 0);
  }, 0);

  return (
    <>
      {/* Interactive Coxcomb Charts Section */}
      <Card
        style={{ marginBottom: 24, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: '8px', background: '#fdfdfd' }}
      >
        <Title level={4} style={{ color: '#34495e', marginBottom: 20 }}>📊 Health Systems & Lifestyle Overview - Interactive Charts</Title>
        <Row gutter={[24, 24]}>
          <Col xs={24} md={12}>
            <SVGCoxcombChart
              healthSystemsData={systemChartData}
              lifestyleDomainsData={[]}
              title="Health Systems Overview"
              chartType="systems"
              systemDeviations={systemDeviations}
              lifestyleDeviations={lifestyleDeviations}
            />
          </Col>
          <Col xs={24} md={12}>
            <SVGCoxcombChart
              healthSystemsData={[]}
              lifestyleDomainsData={lifestyleChartData}
              title="Lifestyle Balance Overview"
              chartType="lifestyle"
              systemDeviations={systemDeviations}
              lifestyleDeviations={lifestyleDeviations}
            />
          </Col>
        </Row>
      </Card>

      <Card style={{ marginBottom: 24, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: '8px' }}>
        <Title level={4} style={{ marginBottom: 20 }}>Health Systems at a Glance</Title>
        <Row gutter={[16, 16]}>
          {systemDeviations && Object.entries(systemDeviations).map(([name, data]) => renderDomainCard(name, data, totalAvailableParams))}
        </Row>
      </Card>
      <Card style={{ marginBottom: 24, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: '8px' }}>
        <Title level={4} style={{ marginBottom: 20 }}>Lifestyle Domains at a Glance</Title>
        <Row gutter={[16, 16]}>
          {lifestyleDeviations && Object.entries(lifestyleDeviations).map(([name, data]) => renderLifestyleDomainCard(name, data))}
        </Row>
      </Card>
      <ParameterModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        title={modalTitle}
        parametersData={modalData}
        priorityThresholds={{
          highPriority: { impactScoreThreshold: 1.5 },
          moderatePriority: { impactScoreThreshold: 0.75 }
        }}
      />
    </>
  );
};

// Error boundary wrapper component
class HealthSystemsOverviewErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[HealthSystemsOverview] Error boundary caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert
          message="Health Systems Overview Error"
          description={`An error occurred while loading the health systems overview. Please refresh the page or contact support if the issue persists. Error: ${this.state.error?.message || 'Unknown error'}`}
          type="error"
          showIcon
          style={{ margin: '20px' }}
          action={
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              style={{ border: 'none', background: 'none', color: '#1890ff', cursor: 'pointer' }}
            >
              Retry
            </button>
          }
        />
      );
    }

    return <HealthSystemsOverview {...this.props} />;
  }
}

export default HealthSystemsOverviewErrorBoundary;
